import { logger } from '@tunasong/models'
import type { BandSpace, Persisted, Profile, ProfileGear, Rider, RiderMember } from '@tunasong/schemas'

/**
 * Extract gear config from a profile's config object
 */
function getProfileGear(profile: Profile): ProfileGear | undefined {
  /**
   * Own profile carries the full config; other members' profiles are returned as public profiles, where
   * publicProfileSchema projects config.gear to a top-level `gear` field. Accept either shape.
   */
  const config = profile.config as Record<string, unknown> | undefined
  const gear = ((profile as Profile & { gear?: ProfileGear }).gear ?? config?.gear) as ProfileGear | undefined

  // Log for debugging
  if (!gear) {
    logger.debug('Profile has no gear config', { userId: profile.userId })
  } else if (!gear.requirements || gear.requirements.length === 0) {
    logger.debug('Profile gear has no requirements', { userId: profile.userId, gear })
  } else {
    logger.debug('Profile gear loaded', {
      userId: profile.userId,
      requirementCount: gear.requirements.length,
      requirements: gear.requirements,
    })
  }

  return gear
}

/**
 * Sync an existing rider with updated profile data
 * Preserves manual edits (channel numbers, role overrides) while updating requirements
 */
export function syncRiderFromProfiles(
  rider: Persisted<Rider>,
  bandspace: Persisted<BandSpace>,
  memberProfiles: Profile[]
): Partial<Rider> {
  const profileMap = new Map(memberProfiles.map(p => [p.userId, p]))

  // Update existing members with fresh profile data
  const updatedMembers: RiderMember[] = (rider.members ?? []).map(member => {
    const profile = profileMap.get(member.userId)
    if (!profile) {
      return member // Keep existing member if profile not found
    }

    const gear = getProfileGear(profile)
    return {
      ...member,
      // Preserve manual overrides if they exist, otherwise use profile data
      role: member.role ?? gear?.role,
      // Update requirements from profile, but preserve existing if profile has none
      requirements: gear?.requirements ?? member.requirements,
    }
  })

  // Add new members that weren't in the rider before
  const existingUserIds = new Set((rider.members ?? []).map(m => m.userId))
  const maxChannel = Math.max(0, ...(rider.members ?? []).map(m => m.channelNumber ?? 0))
  const newMembers: RiderMember[] = memberProfiles
    .filter(profile => !existingUserIds.has(profile.userId))
    .map((profile, index) => {
      const gear = getProfileGear(profile)
      const nextChannelNumber = maxChannel + index + 1
      return {
        userId: profile.userId,
        role: gear?.role,
        requirements: gear?.requirements ?? [],
        channelNumber: nextChannelNumber,
        monitorMix: nextChannelNumber,
      }
    })

  const allMembers = [...updatedMembers, ...newMembers]

  // Re-aggregate monitor needs from member preferences
  const monitorNeeds = memberProfiles.reduce(
    (acc, profile) => {
      const gear = getProfileGear(profile)
      const pref = gear?.monitorPreference
      if (pref === 'wedge' || pref === 'both') {
        acc.wedges = (acc.wedges ?? 0) + 1
      }
      if (pref === 'iem' || pref === 'both') {
        acc.iemPacks = (acc.iemPacks ?? 0) + 1
      }
      return acc
    },
    { wedges: 0, iemPacks: 0, mixes: allMembers.length }
  )

  return {
    members: allMembers,
    shared: {
      ...rider.shared,
      monitors: monitorNeeds,
    },
  }
}
